/**
 * Debug nodes — logging, visualization, profiling.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

// ============================================================================
// LOGGING
// ============================================================================

export const Log: NodeBehaviour = {
  id: 'debug.log',
  name: 'Log',
  category: 'Debug',
  color: '#7f8c8d',
  icon: 'FileText',
  description: 'Logs a message to the console.',
  keywords: ['print', 'console', 'output'],
  inputs: [
    flowIn(),
    input('message', 'Message', 'string', ''),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:log', [ctx.resolveInput('message')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:log', ctx.getInput('message'));
  },
};

export const LogValue: NodeBehaviour = {
  id: 'debug.logValue',
  name: 'Log Value',
  category: 'Debug',
  color: '#7f8c8d',
  icon: 'FileText',
  description: 'Logs a labeled value to the console.',
  keywords: ['print', 'console', 'output', 'variable'],
  inputs: [
    flowIn(),
    input('label', 'Label', 'string', 'Value'),
    input('value', 'Value', 'any', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:logValue', [ctx.resolveInput('label'), ctx.resolveInput('value')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:logValue', ctx.getInput('label'), ctx.getInput('value'));
  },
};

export const LogWarning: NodeBehaviour = {
  id: 'debug.logWarning',
  name: 'Log Warning',
  category: 'Debug',
  color: '#f39c12',
  icon: 'AlertTriangle',
  description: 'Logs a warning message to the console.',
  keywords: ['warn', 'console', 'alert'],
  inputs: [
    flowIn(),
    input('message', 'Message', 'string', ''),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:logWarning', [ctx.resolveInput('message')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:logWarning', ctx.getInput('message'));
  },
};

export const LogError: NodeBehaviour = {
  id: 'debug.logError',
  name: 'Log Error',
  category: 'Debug',
  color: '#e74c3c',
  icon: 'XCircle',
  description: 'Logs an error message to the console.',
  keywords: ['error', 'console', 'fail'],
  inputs: [
    flowIn(),
    input('message', 'Message', 'string', ''),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:logError', [ctx.resolveInput('message')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:logError', ctx.getInput('message'));
  },
};

// ============================================================================
// VISUALIZATION
// ============================================================================

export const DrawLine: NodeBehaviour = {
  id: 'debug.drawLine',
  name: 'Draw Line',
  category: 'Debug',
  color: '#3498db',
  icon: 'Minus',
  description: 'Draws a debug line for one frame.',
  keywords: ['gizmo', 'ray', 'visual'],
  inputs: [
    flowIn(),
    input('x1', 'Start X', 'f32', 0),
    input('y1', 'Start Y', 'f32', 0),
    input('x2', 'End X', 'f32', 1),
    input('y2', 'End Y', 'f32', 1),
    input('color', 'Color', 'color', 0xFF00FF00),
    input('duration', 'Duration (s)', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:drawLine', [
      ctx.resolveInput('x1'), ctx.resolveInput('y1'),
      ctx.resolveInput('x2'), ctx.resolveInput('y2'),
      ctx.resolveInput('color'), ctx.resolveInput('duration'),
    ], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:drawLine', ctx.getInput('x1'), ctx.getInput('y1'), ctx.getInput('x2'), ctx.getInput('y2'), ctx.getInput('color'), ctx.getInput('duration'));
  },
};

export const DrawCircle: NodeBehaviour = {
  id: 'debug.drawCircle',
  name: 'Draw Circle',
  category: 'Debug',
  color: '#3498db',
  icon: 'Circle',
  description: 'Draws a debug circle for one frame.',
  keywords: ['gizmo', 'ring', 'visual'],
  inputs: [
    flowIn(),
    input('x', 'Center X', 'f32', 0),
    input('y', 'Center Y', 'f32', 0),
    input('radius', 'Radius', 'f32', 1),
    input('color', 'Color', 'color', 0xFF00FF00),
    input('duration', 'Duration (s)', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:drawCircle', [
      ctx.resolveInput('x'), ctx.resolveInput('y'),
      ctx.resolveInput('radius'),
      ctx.resolveInput('color'), ctx.resolveInput('duration'),
    ], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:drawCircle', ctx.getInput('x'), ctx.getInput('y'), ctx.getInput('radius'), ctx.getInput('color'), ctx.getInput('duration'));
  },
};

export const DrawRect: NodeBehaviour = {
  id: 'debug.drawRect',
  name: 'Draw Rectangle',
  category: 'Debug',
  color: '#3498db',
  icon: 'Square',
  description: 'Draws a debug rectangle for one frame.',
  keywords: ['gizmo', 'box', 'visual'],
  inputs: [
    flowIn(),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('width', 'Width', 'f32', 1),
    input('height', 'Height', 'f32', 1),
    input('color', 'Color', 'color', 0xFF00FF00),
    input('duration', 'Duration (s)', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:drawRect', [
      ctx.resolveInput('x'), ctx.resolveInput('y'),
      ctx.resolveInput('width'), ctx.resolveInput('height'),
      ctx.resolveInput('color'), ctx.resolveInput('duration'),
    ], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:drawRect', ctx.getInput('x'), ctx.getInput('y'), ctx.getInput('width'), ctx.getInput('height'), ctx.getInput('color'), ctx.getInput('duration'));
  },
};

export const DrawPoint: NodeBehaviour = {
  id: 'debug.drawPoint',
  name: 'Draw Point',
  category: 'Debug',
  color: '#3498db',
  icon: 'MapPin',
  description: 'Draws a debug point/marker for one frame.',
  keywords: ['gizmo', 'marker', 'visual'],
  inputs: [
    flowIn(),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('size', 'Size', 'f32', 0.1),
    input('color', 'Color', 'color', 0xFFFF0000),
    input('duration', 'Duration (s)', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:drawPoint', [
      ctx.resolveInput('x'), ctx.resolveInput('y'),
      ctx.resolveInput('size'),
      ctx.resolveInput('color'), ctx.resolveInput('duration'),
    ], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:drawPoint', ctx.getInput('x'), ctx.getInput('y'), ctx.getInput('size'), ctx.getInput('color'), ctx.getInput('duration'));
  },
};

export const DrawArrow: NodeBehaviour = {
  id: 'debug.drawArrow',
  name: 'Draw Arrow',
  category: 'Debug',
  color: '#3498db',
  icon: 'ArrowRight',
  description: 'Draws a debug arrow (direction indicator).',
  keywords: ['gizmo', 'direction', 'visual', 'vector'],
  inputs: [
    flowIn(),
    input('x', 'Start X', 'f32', 0),
    input('y', 'Start Y', 'f32', 0),
    input('dirX', 'Direction X', 'f32', 1),
    input('dirY', 'Direction Y', 'f32', 0),
    input('length', 'Length', 'f32', 1),
    input('color', 'Color', 'color', 0xFFFFFF00),
    input('duration', 'Duration (s)', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:drawArrow', [
      ctx.resolveInput('x'), ctx.resolveInput('y'),
      ctx.resolveInput('dirX'), ctx.resolveInput('dirY'),
      ctx.resolveInput('length'),
      ctx.resolveInput('color'), ctx.resolveInput('duration'),
    ], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:drawArrow', ctx.getInput('x'), ctx.getInput('y'), ctx.getInput('dirX'), ctx.getInput('dirY'), ctx.getInput('length'), ctx.getInput('color'), ctx.getInput('duration'));
  },
};

export const DrawText: NodeBehaviour = {
  id: 'debug.drawText',
  name: 'Draw Text',
  category: 'Debug',
  color: '#3498db',
  icon: 'Type',
  description: 'Draws debug text at a world position.',
  keywords: ['gizmo', 'label', 'visual'],
  inputs: [
    flowIn(),
    input('text', 'Text', 'string', 'Debug'),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('color', 'Color', 'color', 0xFFFFFFFF),
    input('duration', 'Duration (s)', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:drawText', [
      ctx.resolveInput('text'),
      ctx.resolveInput('x'), ctx.resolveInput('y'),
      ctx.resolveInput('color'), ctx.resolveInput('duration'),
    ], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:drawText', ctx.getInput('text'), ctx.getInput('x'), ctx.getInput('y'), ctx.getInput('color'), ctx.getInput('duration'));
  },
};

// ============================================================================
// PROFILING
// ============================================================================

export const BeginProfiler: NodeBehaviour = {
  id: 'debug.beginProfiler',
  name: 'Begin Profiler',
  category: 'Debug',
  color: '#9b59b6',
  icon: 'Activity',
  description: 'Starts a named profiler section.',
  keywords: ['performance', 'timing', 'measure'],
  inputs: [
    flowIn(),
    input('name', 'Section Name', 'string', 'MySection'),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:beginProfiler', [ctx.resolveInput('name')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:beginProfiler', ctx.getInput('name'));
  },
};

export const EndProfiler: NodeBehaviour = {
  id: 'debug.endProfiler',
  name: 'End Profiler',
  category: 'Debug',
  color: '#9b59b6',
  icon: 'Activity',
  description: 'Ends a named profiler section.',
  keywords: ['performance', 'timing', 'measure'],
  inputs: [
    flowIn(),
    input('name', 'Section Name', 'string', 'MySection'),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:endProfiler', [ctx.resolveInput('name')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:endProfiler', ctx.getInput('name'));
  },
};

// ============================================================================
// ASSERTIONS
// ============================================================================

export const Assert: NodeBehaviour = {
  id: 'debug.assert',
  name: 'Assert',
  category: 'Debug',
  color: '#e74c3c',
  icon: 'AlertOctagon',
  description: 'Logs an error if the condition is false.',
  keywords: ['check', 'verify', 'test'],
  inputs: [
    flowIn(),
    input('condition', 'Condition', 'bool', true),
    input('message', 'Message', 'string', 'Assertion failed'),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:assert', [ctx.resolveInput('condition'), ctx.resolveInput('message')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:assert', ctx.getInput('condition'), ctx.getInput('message'));
  },
};

// ============================================================================
// BREAKPOINT
// ============================================================================

export const Breakpoint: NodeBehaviour = {
  id: 'debug.breakpoint',
  name: 'Breakpoint',
  category: 'Debug',
  color: '#e74c3c',
  icon: 'Pause',
  description: 'Pauses execution in debug mode (like a debugger breakpoint).',
  keywords: ['pause', 'stop', 'debugger'],
  inputs: [flowIn()],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:breakpoint', [], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:breakpoint');
  },
};

// ============================================================================
// STATS
// ============================================================================

export const GetFPS: NodeBehaviour = {
  id: 'debug.getFPS',
  name: 'Get FPS',
  category: 'Debug',
  isPure: true,
  color: '#7f8c8d',
  icon: 'Activity',
  description: 'Gets the current frames per second.',
  keywords: ['performance', 'framerate'],
  inputs: [],
  outputs: [
    output('fps', 'FPS', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getFPS', [], 'f32');
  },
  execute(ctx) {
    ctx.setOutput('fps', ctx.bridge.call('bridge:getFPS') as number ?? 0);
  },
};

export const GetEntityCount: NodeBehaviour = {
  id: 'debug.getEntityCount',
  name: 'Get Entity Count',
  category: 'Debug',
  isPure: true,
  color: '#7f8c8d',
  icon: 'Users',
  description: 'Gets the total number of active entities.',
  keywords: ['count', 'stats', 'objects'],
  inputs: [],
  outputs: [
    output('count', 'Count', 'i32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getEntityCount', [], 'i32');
  },
  execute(ctx) {
    ctx.setOutput('count', ctx.bridge.call('bridge:getEntityCount') as number ?? 0);
  },
};

// ============================================================================
// EXPORT
// ============================================================================

export const DEBUG_NODES: NodeBehaviour[] = [
  // Logging
  Log,
  LogValue,
  LogWarning,
  LogError,
  // Visualization
  DrawLine,
  DrawCircle,
  DrawRect,
  DrawPoint,
  DrawArrow,
  DrawText,
  // Profiling
  BeginProfiler,
  EndProfiler,
  // Assertions
  Assert,
  Breakpoint,
  // Stats
  GetFPS,
  GetEntityCount,
];
